import { useEffect, useRef, useState } from "react";

function useAppLoader(routeKey) {
  const [isLoaderVisible, setIsLoaderVisible] = useState(true);
  const [isLoaderFinishing, setIsLoaderFinishing] = useState(false);
  const [isBootstrapped, setIsBootstrapped] = useState(false);
  const previousRouteKeyRef = useRef(routeKey);

  useEffect(() => {
    let isCancelled = false;

    const waitForImages = () => {
      const images = Array.from(document.images || []);
      if (!images.length) return Promise.resolve();

      return Promise.all(
        images.map(
          (img) =>
            new Promise((resolve) => {
              if (img.complete) {
                resolve();
                return;
              }

              const done = () => {
                img.removeEventListener("load", done);
                img.removeEventListener("error", done);
                resolve();
              };

              img.addEventListener("load", done, { once: true });
              img.addEventListener("error", done, { once: true });
            })
        )
      );
    };

    const waitForFonts = () =>
      document.fonts?.ready instanceof Promise
        ? document.fonts.ready.catch(() => undefined)
        : Promise.resolve();

    const waitForWindowLoad = () =>
      document.readyState === "complete"
        ? Promise.resolve()
        : new Promise((resolve) => window.addEventListener("load", resolve, { once: true }));

    const runLoader = async () => {
      await Promise.all([waitForWindowLoad(), waitForFonts(), waitForImages()]);
      if (isCancelled) return;

      setIsLoaderFinishing(true);
      setTimeout(() => {
        if (!isCancelled) {
          setIsLoaderVisible(false);
          setIsBootstrapped(true);
        }
      }, 450);
    };

    runLoader();

    return () => {
      isCancelled = true;
    };
  }, []);

  useEffect(() => {
    if (!isBootstrapped) return;
    if (previousRouteKeyRef.current === routeKey) return;
    previousRouteKeyRef.current = routeKey;

    setIsLoaderVisible(true);
    setIsLoaderFinishing(false);

    const finishTimer = setTimeout(() => {
      setIsLoaderFinishing(true);
    }, 180);

    const hideTimer = setTimeout(() => {
      setIsLoaderVisible(false);
    }, 520);

    return () => {
      clearTimeout(finishTimer);
      clearTimeout(hideTimer);
    };
  }, [isBootstrapped, routeKey]);

  return { isLoaderVisible, isLoaderFinishing };
}

export default useAppLoader;
